import React, { useState, useEffect, useRef } from 'react';

// ── System design snippets to cycle through ─────────────────────────
const SNIPPETS = [
  {
    filename: 'load_balancer.py',
    lines: [
      { num: 1, tokens: [{ t: 'keyword', v: 'class' }, { t: 'space' }, { t: 'class', v: 'LoadBalancer' }, { t: 'plain', v: ':' }] },
      { num: 2, tokens: [{ t: 'indent1' }, { t: 'keyword', v: 'def' }, { t: 'space' }, { t: 'func', v: 'distribute' }, { t: 'plain', v: '(' }, { t: 'param', v: 'self' }, { t: 'plain', v: ', ' }, { t: 'param', v: 'request' }, { t: 'plain', v: '):' }] },
      { num: 3, tokens: [{ t: 'indent2' }, { t: 'param', v: 'server' }, { t: 'plain', v: ' = self.' }, { t: 'func', v: 'round_robin' }, { t: 'plain', v: '()' }] },
      { num: 4, tokens: [{ t: 'indent2' }, { t: 'keyword', v: 'return' }, { t: 'space' }, { t: 'param', v: 'server' }, { t: 'plain', v: '.' }, { t: 'func', v: 'handle' }, { t: 'plain', v: '(' }, { t: 'param', v: 'request' }, { t: 'plain', v: ')' }] },
      { num: 5, tokens: [{ t: 'comment', v: '  # ✦ Distributes across healthy nodes' }] },
    ],
  },
  {
    filename: 'consistent_hash.py',
    lines: [
      { num: 1, tokens: [{ t: 'keyword', v: 'class' }, { t: 'space' }, { t: 'class', v: 'ConsistentHash' }, { t: 'plain', v: ':' }] },
      { num: 2, tokens: [{ t: 'indent1' }, { t: 'keyword', v: 'def' }, { t: 'space' }, { t: 'func', v: 'get_node' }, { t: 'plain', v: '(' }, { t: 'param', v: 'self' }, { t: 'plain', v: ', ' }, { t: 'param', v: 'key' }, { t: 'plain', v: '):' }] },
      { num: 3, tokens: [{ t: 'indent2' }, { t: 'param', v: 'h' }, { t: 'plain', v: ' = self.' }, { t: 'func', v: 'hash' }, { t: 'plain', v: '(' }, { t: 'param', v: 'key' }, { t: 'plain', v: ')' }] },
      { num: 4, tokens: [{ t: 'indent2' }, { t: 'keyword', v: 'return' }, { t: 'space' }, { t: 'plain', v: 'self.' }, { t: 'func', v: 'ring' }, { t: 'plain', v: '[' }, { t: 'param', v: 'h' }, { t: 'plain', v: ']' }] },
      { num: 5, tokens: [{ t: 'comment', v: '  # ✦ Minimal remapping on scale' }] },
    ],
  },
  {
    filename: 'message_queue.py',
    lines: [
      { num: 1, tokens: [{ t: 'keyword', v: 'class' }, { t: 'space' }, { t: 'class', v: 'MessageQueue' }, { t: 'plain', v: ':' }] },
      { num: 2, tokens: [{ t: 'indent1' }, { t: 'keyword', v: 'def' }, { t: 'space' }, { t: 'func', v: 'publish' }, { t: 'plain', v: '(' }, { t: 'param', v: 'self' }, { t: 'plain', v: ', ' }, { t: 'param', v: 'event' }, { t: 'plain', v: '):' }] },
      { num: 3, tokens: [{ t: 'indent2' }, { t: 'plain', v: 'self.' }, { t: 'param', v: 'broker' }, { t: 'plain', v: '.' }, { t: 'func', v: 'send' }, { t: 'plain', v: '(' }, { t: 'param', v: 'event' }, { t: 'plain', v: ')' }] },
      { num: 4, tokens: [{ t: 'indent1' }, { t: 'keyword', v: 'def' }, { t: 'space' }, { t: 'func', v: 'consume' }, { t: 'plain', v: '(' }, { t: 'param', v: 'self' }, { t: 'plain', v: '):' }] },
      { num: 5, tokens: [{ t: 'indent2' }, { t: 'keyword', v: 'return' }, { t: 'space' }, { t: 'plain', v: 'self.' }, { t: 'param', v: 'broker' }, { t: 'plain', v: '.' }, { t: 'func', v: 'poll' }, { t: 'plain', v: '()' }] },
    ],
  },
  {
    filename: 'cache_layer.py',
    lines: [
      { num: 1, tokens: [{ t: 'keyword', v: 'class' }, { t: 'space' }, { t: 'class', v: 'CacheLayer' }, { t: 'plain', v: ':' }] },
      { num: 2, tokens: [{ t: 'indent1' }, { t: 'keyword', v: 'def' }, { t: 'space' }, { t: 'func', v: 'get' }, { t: 'plain', v: '(' }, { t: 'param', v: 'self' }, { t: 'plain', v: ', ' }, { t: 'param', v: 'key' }, { t: 'plain', v: '):' }] },
      { num: 3, tokens: [{ t: 'indent2' }, { t: 'keyword', v: 'if' }, { t: 'space' }, { t: 'param', v: 'key' }, { t: 'plain', v: ' in self.' }, { t: 'func', v: 'redis' }, { t: 'plain', v: ':' }] },
      { num: 4, tokens: [{ t: 'indent3' }, { t: 'keyword', v: 'return' }, { t: 'space' }, { t: 'plain', v: 'self.' }, { t: 'func', v: 'redis' }, { t: 'plain', v: '.' }, { t: 'func', v: 'get' }, { t: 'plain', v: '(' }, { t: 'param', v: 'key' }, { t: 'plain', v: ')' }] },
      { num: 5, tokens: [{ t: 'comment', v: '  # ✦ Cache-aside pattern' }] },
    ],
  },
  {
    filename: 'api_gateway.py',
    lines: [
      { num: 1, tokens: [{ t: 'keyword', v: 'class' }, { t: 'space' }, { t: 'class', v: 'APIGateway' }, { t: 'plain', v: ':' }] },
      { num: 2, tokens: [{ t: 'indent1' }, { t: 'keyword', v: 'def' }, { t: 'space' }, { t: 'func', v: 'route' }, { t: 'plain', v: '(' }, { t: 'param', v: 'self' }, { t: 'plain', v: ', ' }, { t: 'param', v: 'req' }, { t: 'plain', v: '):' }] },
      { num: 3, tokens: [{ t: 'indent2' }, { t: 'plain', v: 'self.' }, { t: 'func', v: 'authenticate' }, { t: 'plain', v: '(' }, { t: 'param', v: 'req' }, { t: 'plain', v: ')' }] },
      { num: 4, tokens: [{ t: 'indent2' }, { t: 'plain', v: 'self.' }, { t: 'func', v: 'rate_limit' }, { t: 'plain', v: '(' }, { t: 'param', v: 'req' }, { t: 'plain', v: ')' }] },
      { num: 5, tokens: [{ t: 'indent2' }, { t: 'keyword', v: 'return' }, { t: 'space' }, { t: 'plain', v: 'self.' }, { t: 'func', v: 'forward' }, { t: 'plain', v: '(' }, { t: 'param', v: 'req' }, { t: 'plain', v: ')' }] },
    ],
  },
];

// ── Token renderer ───────────────────────────────
const renderToken = (tok, i) => {
  switch (tok.t) {
    case 'keyword':  return <span key={i} className="code-keyword">{tok.v}</span>;
    case 'class':    return <span key={i} className="code-class">{tok.v}</span>;
    case 'func':     return <span key={i} className="code-function">{tok.v}</span>;
    case 'param':    return <span key={i} className="code-param">{tok.v}</span>;
    case 'comment':  return <span key={i} className="code-comment">{tok.v}</span>;
    case 'space':    return <span key={i}> </span>;
    case 'indent1':  return <span key={i} className="indent" />;
    case 'indent2':  return <span key={i} className="indent2" />;
    case 'indent3':  return <span key={i} className="indent3" />;
    default:         return <span key={i}>{tok.v}</span>;
  }
};

// ── Typing effect hook ───────────────────────────
// Returns how many "chars" of the flattened text are currently visible
function useTypingCycle(snippets, typingSpeed = 28, pauseMs = 2200, eraseSpeed = 12) {
  const [snippetIdx, setSnippetIdx] = useState(0);
  const [visibleChars, setVisibleChars] = useState(0);
  const [phase, setPhase] = useState('typing'); // 'typing' | 'pause' | 'erasing'
  const rafRef = useRef(null);
  const lastRef = useRef(null);

  const totalChars = (idx) =>
    snippets[idx].lines.reduce(
      (s, l) => s + l.tokens.reduce((a, t) => a + (t.v ? t.v.length : 0), 0),
      0
    );

  useEffect(() => {
    let chars = 0;
    let currentPhase = 'typing';
    let currentIdx = 0;
    let pauseTimer = null;

    const tick = (now) => {
      if (!lastRef.current) lastRef.current = now;
      const elapsed = now - lastRef.current;

      if (currentPhase === 'typing') {
        const charsToAdd = Math.floor(elapsed / typingSpeed);
        if (charsToAdd > 0) {
          chars = Math.min(chars + charsToAdd, totalChars(currentIdx));
          setVisibleChars(chars);
          lastRef.current = now;
          if (chars >= totalChars(currentIdx)) {
            currentPhase = 'pause';
            setPhase('pause');
            pauseTimer = setTimeout(() => {
              currentPhase = 'erasing';
              setPhase('erasing');
              lastRef.current = null;
              rafRef.current = requestAnimationFrame(tick);
            }, pauseMs);
            return;
          }
        }
      } else if (currentPhase === 'erasing') {
        const charsToRemove = Math.floor(elapsed / eraseSpeed);
        if (charsToRemove > 0) {
          chars = Math.max(chars - charsToRemove, 0);
          setVisibleChars(chars);
          lastRef.current = now;
          if (chars <= 0) {
            currentIdx = (currentIdx + 1) % snippets.length;
            setSnippetIdx(currentIdx);
            currentPhase = 'typing';
            setPhase('typing');
            lastRef.current = null;
          }
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(pauseTimer);
    };
    // eslint-disable-next-line
  }, []);

  return { snippetIdx, visibleChars, phase };
}

// ── Slice visible chars across lines ────────────
function getVisibleLines(snippet, visibleChars) {
  let remaining = visibleChars;
  return snippet.lines.map((line) => {
    if (remaining <= 0) return { ...line, tokens: [] };
    const lineText = line.tokens.reduce((s, t) => s + (t.v ? t.v.length : 0), 0);
    if (remaining >= lineText) {
      remaining -= lineText;
      return line;
    }
    // Partial line
    let partialRemaining = remaining;
    remaining = 0;
    const partialTokens = line.tokens.map((tok) => {
      if (!tok.v || partialRemaining <= 0) return null;
      if (partialRemaining >= tok.v.length) {
        partialRemaining -= tok.v.length;
        return tok;
      }
      const partial = { ...tok, v: tok.v.slice(0, partialRemaining) };
      partialRemaining = 0;
      return partial;
    }).filter(Boolean);
    return { ...line, tokens: partialTokens, partial: true };
  });
}

// ── Hero component ───────────────────────────────
const Hero = () => {
  const { snippetIdx, visibleChars, phase } = useTypingCycle(SNIPPETS);
  const snippet = SNIPPETS[snippetIdx];
  const visibleLines = getVisibleLines(snippet, visibleChars);
  const showCursor = phase !== 'pause';

  return (
    <section className="hero">
      {/* Floating particles */}
      <div className="particles">
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
      </div>

      <div className="hero-grid">
        <div className="hero-content">
          <div className="badge-floating">The way engineers actually think about systems</div>
          <h1>
            System Design,<br />
            <span className="hero-h1-gradient">Done Right</span>
          </h1>
          <p>
            Most system design prep is surface-level memorization. We teach you a structured way to reason through any problem — from first principles to production-scale trade-offs.
          </p>
          <div className="hero-buttons">
            <a href="#blueprint" className="btn btn-primary">
              See How It Works
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
            <a href="#videos" className="btn btn-secondary">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M5 3.5l8 4.5-8 4.5V3.5z" fill="currentColor"/>
              </svg>
              Watch Free Preview
            </a>
          </div>

          {/* Value props — what you actually get */}
          <div className="achievement-badges">
            <div className="badge-item">
              <div className="badge-icon">🧠</div>
              <span>First Principles</span>
            </div>
            <div className="badge-item">
              <div className="badge-icon">🗺️</div>
              <span>One Framework</span>
            </div>
            <div className="badge-item">
              <div className="badge-icon">⚙️</div>
              <span>Real Systems</span>
            </div>
          </div>
        </div>

        <div className="hero-visual">
          <div className="hero-card">
            <div className="code-window">
              <div className="code-header">
                <div className="dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="code-title">{snippet.filename}</span>
              </div>
              <div className="code-content hero-code-content">
                {snippet.lines.map((line, li) => {
                  const visible = visibleLines[li];
                  const hasContent = visible && visible.tokens.length > 0;
                  const isLastVisible = hasContent &&
                    li === visibleLines.map((l, i) => l.tokens.length > 0 ? i : -1).filter(i => i >= 0).slice(-1)[0];
                  return (
                    <div key={li} className="code-line" style={{ visibility: hasContent ? 'visible' : 'hidden' }}>
                      <span className="line-number">{line.num}</span>
                      <span className="code-line-body">
                        {hasContent && visible.tokens.map((tok, ti) => renderToken(tok, ti))}
                        {isLastVisible && showCursor && (
                          <span className="hero-cursor">|</span>
                        )}
                      </span>
                    </div>
                  );
                })}
                {/* Snippet label pill */}
                <div className="hero-snippet-pills">
                  {SNIPPETS.map((s, i) => (
                    <span
                      key={i}
                      className={`hero-snippet-dot ${i === snippetIdx ? 'active' : ''}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
